/**
 * «Какое оборудование чинили» — блок в форме Работы.
 *
 * Связь many-to-many: выезд «почистить три печи» существует, и одного поля
 * тут мало. Но по умолчанию блок компактный — чипы выбранного плюс кнопка
 * «добавить»: в большинстве Работ единица одна, и полноценный мультиселект
 * только мешал бы.
 *
 * Право на связывание берётся от Работы (tasks.edit), а не от оборудования:
 * «указать, что чинили» — действие над Работой. У Работника equipment.edit
 * ограничен собственным черновиком, и по нему он не смог бы отметить
 * починенную печь.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import Icon from './Icon';
import { usePerm } from '../hooks/usePerm';
import { track } from '../analytics';

export default function TaskEquipmentPicker({ taskId, propertyId, canEdit, pending, onPendingChange }) {
  const { t, i18n } = useTranslation();
  const { can } = usePerm();
  const lang = i18n.language === 'en' ? 'en' : 'ru';

  const [all, setAll] = useState(null);      // парк Объекта
  const [linked, setLinked] = useState([]);  // id привязанных
  const [adding, setAdding] = useState(false);

  const loadPark = useCallback(() => {
    if (!propertyId || !can('equipment.view')) { setAll({ applicable: false, items: [] }); return; }
    api.equipmentList(propertyId, lang)
      .then(setAll)
      .catch(() => setAll({ applicable: false, items: [] }));
  }, [propertyId, lang, can]);

  // У новой Работы привязок на сервере ещё нет — выбор живёт в родителе и
  // досылается после сохранения. Поэтому здесь два режима: сохранённая Работа
  // ходит в сеть, несохранённая работает от pending.
  const loadLinked = useCallback(() => {
    if (!taskId) { setLinked(pending || []); return; }
    api.equipmentByTask(taskId)
      .then((d) => setLinked((Array.isArray(d) ? d : []).map((x) => x.id)))
      .catch(() => setLinked([]));
  }, [taskId, pending]);

  useEffect(() => { loadPark(); }, [loadPark]);
  useEffect(() => { loadLinked(); }, [loadLinked]);

  // Блок не показывается на жилье и там, где парк пуст: пустой селект в форме
  // Работы — это шум, а завести оборудование отсюда всё равно нельзя.
  if (!all || !all.applicable) return null;
  const items = all.items || [];
  if (items.length === 0) return null;

  const nameOf = (e) => e.name_tr || e.name;
  const chosen = items.filter((e) => linked.includes(e.id));
  const rest = items.filter((e) => !linked.includes(e.id));

  const link = async (id) => {
    setLinked((prev) => [...prev, id]);          // оптимистично: форма не должна ждать сеть
    setAdding(false);

    if (!taskId) {                               // Работа ещё не создана — копим
      onPendingChange?.([...(pending || []), id]);
      track('equipment_linked_to_task', { task_id: null, equipment_id: id, deferred: true });
      return;
    }
    try {
      await api.equipmentLink(taskId, id);
      track('equipment_linked_to_task', { task_id: taskId, equipment_id: id });
    } catch {
      setLinked((prev) => prev.filter((x) => x !== id));
    }
  };

  const unlink = async (id) => {
    setLinked((prev) => prev.filter((x) => x !== id));

    if (!taskId) {
      onPendingChange?.((pending || []).filter((x) => x !== id));
      return;
    }
    try {
      await api.equipmentUnlink(taskId, id);
    } catch {
      setLinked((prev) => [...prev, id]);
    }
  };

  return (
    <div className="form-group">
      <label className="form-label">{t('equipment.inTaskLabel')}</label>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {chosen.map((e) => (
          <span key={e.id} className="badge badge-neutral"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 8px', fontSize: 13 }}>
            <Icon name="tool" size={13} />
            {nameOf(e)}
            {canEdit && (
              <button type="button"
                      style={{ background: 'none', border: 0, padding: 0, margin: 0, lineHeight: 0,
                               cursor: 'pointer', color: 'inherit' }}
                      onClick={() => unlink(e.id)} aria-label={t('equipment.unlink')}>
                <Icon name="x" size={13} />
              </button>
            )}
          </span>
        ))}

        {canEdit && rest.length > 0 && !adding && (
          <button type="button" className="btn btn-sm" data-ph-capture="equipment_link_open"
                  onClick={() => setAdding(true)}>
            <Icon name="plus" size={13} /> {chosen.length ? t('equipment.addMore') : t('equipment.pick')}
          </button>
        )}
      </div>

      {adding && (
        <select
          className="form-select"
          style={{ marginTop: 8 }}
          defaultValue=""
          autoFocus
          onChange={(e) => e.target.value && link(Number(e.target.value))}
          onBlur={() => setAdding(false)}
        >
          <option value="">{t('equipment.pick')}</option>
          {rest.map((e) => (
            <option key={e.id} value={e.id}>
              {nameOf(e)}{e.model ? ` · ${e.model}` : ''}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
